"use client";
import {
    Circle,
    Document,
    Image,
    Page,
    Path,
    Svg,
    Text,
    View,
} from "@react-pdf/renderer";
import { pdf } from "@react-pdf/renderer";
import { useTranslations } from "next-intl";
import { useEffect, useMemo, useState } from "react";
import moment from "moment";
import QRCode from "qrcode";
import { AnimatePresence, motion } from "motion/react";
import CertificationLoader from "./CertificationLoader";
import BottomSheetModal from "@/components/ui/BottomSheetModal/BottomSheetModal";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";
import { capitalize } from "@/helpers/capitalize";
import { ACCENT, DOUX, ENCRE, TEXTE, asset } from "@/components/pdf/PdfBrand";
import { FondPastel, LAVANDE_FILET } from "@/components/pdf/PdfPastel";

// Le certificat d'une formation, à la charte pastel des accélérateurs
// (components/pdf/PdfPastel) depuis le 30/09/2026. La composition reste celle
// d'un diplôme (centrée, un sceau, une signature) : le pastel passe par le
// fond à taches qui fait passe-partout et par le sceau, dans la couleur
// principale. Le document est toujours en anglais, quelle que soit la langue
// de la page ; un QR code mène à la page publique du certificat.
//
// A4 paysage, un point de moins que la page : à la taille exacte, react-pdf
// pousse le dessin sur une deuxième page.
const PAGE = { largeur: 841, hauteur: 594 };
const CADRE = 22;
const CARTE = 32;

// L'adresse publique d'un certificat, celle du QR code et du pied de page.
export const certificateUrl = (number) =>
    `https://believemy.com/certifications/${number}`;

// Le QR code du certificat, en noir : seul son cadre prend la couleur
// secondaire.
export const certificateQrCode = (number) =>
    QRCode.toDataURL(certificateUrl(number), {
        margin: 0,
        width: 360,
        errorCorrectionLevel: "M",
        color: { dark: "#0B1020", light: "#FFFFFF" },
    });

// Les taches de couleur du fond : [couleur, x, y, rayon].
const TACHES_PAYSAGE = [
    ["#E4DCFF", 40, 40, 300],
    ["#D8ECFF", 820, 90, 280],
    ["#FFE6D9", 760, 580, 260],
    ["#DDF6EA", 90, 570, 260],
    ["#FFF1C9", 420, 610, 200],
];

// La couleur secondaire de Believemy (#FFA28B), trop pâle pour un texte fin
// sur fond blanc : les textes prennent la même teinte, assombrie pour rester
// lisibles (contraste d'environ 4 pour 1).
export const SECONDAIRE = "#FFA28B";
export const SECONDAIRE_TEXTE = "#C4553D";

// La couleur principale en pastel, pour le sceau.
const PECHE = "#FFE6D9";
const PECHE_FILET = "#F7B79C";

// Les deux blocs du bas, de part et d'autre du sceau.
const BLOC = { largeur: 220, hauteur: 72 };

const libelle = {
    fontFamily: "SourceCode",
    fontSize: 8,
    lineHeight: 1,
    letterSpacing: 1.4,
    textTransform: "uppercase",
    color: SECONDAIRE_TEXTE,
    marginBottom: 5,
};

// Le sceau : une médaille pêche dont la couronne porte un texte circulaire,
// comme l'ancien tampon « Believemy Certified », la flamme au centre.
// react-pdf ne sait pas écrire le long d'un chemin : chaque lettre est posée
// à son angle.
const SCEAU_TEXTE = "BELIEVEMY • CERTIFIED • SINCE 2018 • ";

function Sceau({ taille = 122 }) {
    const c = taille / 2;
    const rayon = c - 11;
    const lettres = [...SCEAU_TEXTE];
    const pas = 360 / lettres.length;
    return (
        <View style={{ width: taille, height: taille, position: "relative" }}>
            <Svg
                width={taille}
                height={taille}
                style={{ position: "absolute", top: 0, left: 0 }}
            >
                <Circle cx={c} cy={c} r={c} fill={PECHE} />
                <Circle cx={c} cy={c} r={c - 3} fill="none" stroke={PECHE_FILET} strokeWidth={0.8} />
                <Circle cx={c} cy={c} r={c - 20} fill="#FFFFFF" />
                <Circle
                    cx={c}
                    cy={c}
                    r={c - 24}
                    fill="none"
                    stroke={ACCENT}
                    strokeWidth={1.2}
                    strokeDasharray="1.5 3.5"
                    strokeLinecap="round"
                />
            </Svg>
            {lettres.map((lettre, i) => {
                const angle = i * pas;
                const rad = (angle * Math.PI) / 180;
                return (
                    <View
                        key={i}
                        style={{
                            position: "absolute",
                            left: c + rayon * Math.sin(rad) - 5,
                            top: c - rayon * Math.cos(rad) - 5,
                            width: 10,
                            height: 10,
                            alignItems: "center",
                            justifyContent: "center",
                            transform: `rotate(${angle}deg)`,
                        }}
                    >
                        <Text
                            style={{
                                fontFamily: "SourceCode",
                                fontSize: 7.2,
                                lineHeight: 1,
                                color: ACCENT,
                            }}
                        >
                            {lettre}
                        </Text>
                    </View>
                );
            })}
            <Image
                src={asset("/images/site/brand/fire.png")}
                style={{
                    position: "absolute",
                    width: taille * 0.22,
                    left: c - taille * 0.11,
                    top: c - taille * 0.148,
                }}
            />
        </View>
    );
}

// Le filigrane, en deux éléments inspirés des papiers de sécurité :
// - une rosace guillochée centrée sur le nom, faite de courbes entrelacées
//   dont le centre reste vide pour ne pas passer sous le texte ;
// - une micro-impression qui fait le tour de la carte : de loin un simple
//   filet, de près une ligne de texte minuscule.
const CARTE_TAILLE = {
    largeur: PAGE.largeur + 1 - 2 * CARTE,
    hauteur: PAGE.hauteur + 1 - 2 * CARTE,
};
// Au centre exact de la carte.
const ROSACE_CENTRE = { x: CARTE_TAILLE.largeur / 2, y: CARTE_TAILLE.hauteur / 2 };

// Une bande de la rosace : `lignes` courbes fermées r(θ) = rayon +
// amplitude × sin(lobes × θ + décalage), décalées les unes des autres pour
// s'entrelacer.
function bande({ rayon, amplitude, lobes, lignes }) {
    const { x: cx, y: cy } = ROSACE_CENTRE;
    const chemins = [];
    for (let j = 0; j < lignes; j++) {
        const decalage = (j * 2 * Math.PI) / lignes;
        let d = "";
        // Une vingtaine de points par lobe suffit à une courbe lisse.
        const pas = lobes * 20;
        for (let i = 0; i <= pas; i++) {
            const theta = (i / pas) * 2 * Math.PI;
            const r = rayon + amplitude * Math.sin(lobes * theta + decalage);
            const x = cx + r * Math.cos(theta);
            const y = cy + r * Math.sin(theta);
            d += `${i === 0 ? "M" : "L"}${x.toFixed(2)},${y.toFixed(2)} `;
        }
        chemins.push(`${d}Z`);
    }
    return chemins;
}

const ROSACE = [
    { chemins: bande({ rayon: 200, amplitude: 13, lobes: 36, lignes: 6 }), couleur: "#B9A7FF", opacite: 0.4 },
    { chemins: bande({ rayon: 215, amplitude: 11, lobes: 24, lignes: 4 }), couleur: "#F4A889", opacite: 0.35 },
];

// La micro-impression : la phrase est répétée autant de fois qu'il faut, et
// l'espacement des lettres est calculé pour que chaque ligne remplisse
// exactement son côté et se termine sur une phrase entière. Un losange
// referme chaque angle.
const MICRO_PHRASE = "BELIEVEMY CERTIFIED • AUTHENTIC DOCUMENT • VERIFY AT BELIEVEMY.COM";
const MICRO_TAILLE = 2.6;
// Source Code Pro est à chasse fixe : 600 unités sur 1 000 par caractère.
const MICRO_AVANCE = MICRO_TAILLE * 0.6;

function ligneMicro(longueur) {
    const pasVoulu = MICRO_AVANCE + 0.4;
    const parPhrase = MICRO_PHRASE.length + 3;
    const repetitions = Math.max(1, Math.round((longueur / pasVoulu + 3) / parPhrase));
    const texte = Array(repetitions).fill(MICRO_PHRASE).join(" • ");
    return {
        texte,
        letterSpacing: longueur / [...texte].length - MICRO_AVANCE,
    };
}

function Filigrane() {
    const { largeur, hauteur } = CARTE_TAILLE;
    const bord = 11;
    // Le vide laissé de part et d'autre de chaque losange.
    const ecart = 5;
    const longueurHaut = largeur - 2 * (bord + ecart);
    const longueurCote = hauteur - 2 * (bord + ecart);
    const haut = ligneMicro(longueurHaut);
    const cote = ligneMicro(longueurCote);
    const microStyle = {
        fontFamily: "SourceCode",
        fontSize: MICRO_TAILLE,
        lineHeight: 1,
        color: SECONDAIRE_TEXTE,
        opacity: 0.5,
        maxLines: 1,
    };
    const losange = 3.2;
    return (
        <>
            <Svg
                width={largeur}
                height={hauteur}
                style={{ position: "absolute", top: 0, left: 0 }}
            >
                {ROSACE.flatMap(({ chemins, couleur, opacite }, b) =>
                    chemins.map((d, i) => (
                        <Path
                            key={`${b}-${i}`}
                            d={d}
                            fill="none"
                            stroke={couleur}
                            strokeWidth={0.3}
                            strokeOpacity={opacite}
                        />
                    )),
                )}
                <Circle
                    cx={ROSACE_CENTRE.x}
                    cy={ROSACE_CENTRE.y}
                    r={172}
                    fill="none"
                    stroke="#C9BCFF"
                    strokeWidth={0.4}
                    strokeOpacity={0.5}
                />
            </Svg>

            {/* En haut et en bas */}
            {[bord, hauteur - bord].map((y) => (
                <Text
                    key={y}
                    style={{
                        ...microStyle,
                        letterSpacing: haut.letterSpacing,
                        position: "absolute",
                        top: y - MICRO_TAILLE / 2,
                        left: bord + ecart,
                        width: longueurHaut + 2,
                    }}
                >
                    {haut.texte}
                </Text>
            ))}
            {/* Sur les côtés, tournés d'un quart de tour */}
            {[bord, largeur - bord].map((x, i) => (
                <View
                    key={x}
                    style={{
                        position: "absolute",
                        left: x - longueurCote / 2,
                        top: hauteur / 2 - 4,
                        width: longueurCote,
                        height: 8,
                        justifyContent: "center",
                        transform: `rotate(${i === 0 ? -90 : 90}deg)`,
                    }}
                >
                    <Text style={{ ...microStyle, letterSpacing: cote.letterSpacing, width: longueurCote + 2 }}>
                        {cote.texte}
                    </Text>
                </View>
            ))}
            {/* Les losanges des angles */}
            {[
                [bord, bord],
                [largeur - bord, bord],
                [bord, hauteur - bord],
                [largeur - bord, hauteur - bord],
            ].map(([x, y]) => (
                <View
                    key={`${x}-${y}`}
                    style={{
                        position: "absolute",
                        left: x - losange / 2,
                        top: y - losange / 2,
                        width: losange,
                        height: losange,
                        backgroundColor: SECONDAIRE,
                        transform: "rotate(45deg)",
                    }}
                />
            ))}
        </>
    );
}

export const Certification = ({ certification, qrCode }) => {
    const name = capitalize(
        [certification.user.firstName, certification.user.lastName]
            .filter(Boolean)
            .join(" "),
    );
    const issued = moment(certification.dateCreation)
        .locale("en")
        .format("MMMM D, YYYY");
    const url = certificateUrl(certification.certificationNumber);

    return (
        <Document title={`Believemy certificate of ${name}`}>
            <Page
                size={[PAGE.largeur + 1, PAGE.hauteur + 1]}
                style={{ fontFamily: "SourceSans", color: TEXTE }}
            >
                <FondPastel
                    taches={TACHES_PAYSAGE}
                    largeur={PAGE.largeur + 1}
                    hauteur={PAGE.hauteur + 1}
                    id="certif"
                />

                {/* Le filet du passe-partout, puis la carte */}
                <View
                    style={{
                        position: "absolute",
                        top: CADRE,
                        left: CADRE,
                        right: CADRE,
                        bottom: CADRE,
                        borderWidth: 0.75,
                        borderColor: "#FFFFFF",
                        borderRadius: 20,
                    }}
                />
                <View
                    style={{
                        position: "absolute",
                        top: CARTE,
                        left: CARTE,
                        right: CARTE,
                        bottom: CARTE,
                        backgroundColor: "rgba(255,255,255,0.88)",
                        borderRadius: 14,
                        overflow: "hidden",
                        paddingTop: 40,
                        paddingHorizontal: 60,
                        alignItems: "center",
                    }}
                >
                    <Filigrane />
                    <Image
                        src={asset("/images/site/brand/believemy-new-logo-full-blackx1400.png")}
                        style={{ width: 112, marginBottom: 34 }}
                    />

                    <Text
                        style={{
                            fontFamily: "SourceCode",
                            fontSize: 10,
                            lineHeight: 1,
                            letterSpacing: 3,
                            textTransform: "uppercase",
                            color: SECONDAIRE_TEXTE,
                            marginBottom: 22,
                        }}
                    >
                        Certificate of completion
                    </Text>

                    <Text style={{ fontSize: 12, color: DOUX, marginBottom: 8 }}>
                        This certificate is proudly awarded to
                    </Text>
                    <Text
                        style={{
                            fontFamily: "Geist",
                            fontSize: name.length > 26 ? 40 : 50,
                            lineHeight: 1.08,
                            color: ENCRE,
                            textAlign: "center",
                            maxWidth: 660,
                        }}
                    >
                        {name}
                    </Text>
                    <View
                        style={{
                            width: 44,
                            height: 3,
                            borderRadius: 2,
                            backgroundColor: ACCENT,
                            marginTop: 16,
                            marginBottom: 16,
                        }}
                    />
                    <Text style={{ fontSize: 12, color: DOUX, marginBottom: 6 }}>
                        for successfully completing the course
                    </Text>
                    <Text
                        style={{
                            fontWeight: 600,
                            fontSize: 19,
                            lineHeight: 1.3,
                            color: SECONDAIRE_TEXTE,
                            textAlign: "center",
                            maxWidth: 560,
                        }}
                    >
                        {certification.course.name}
                    </Text>

                    {/* QR code et repères, sceau, signature : les deux blocs ont
                        la même largeur et la même hauteur que le QR code, et sont
                        centrés sur l'axe du sceau, à égale distance des bords. */}
                    <View
                        style={{
                            position: "absolute",
                            left: 60,
                            right: 60,
                            bottom: 34,
                            flexDirection: "row",
                            alignItems: "center",
                            justifyContent: "space-between",
                        }}
                    >
                        <View style={{ width: BLOC.largeur, height: BLOC.hauteur, flexDirection: "row" }}>
                            {qrCode && (
                                <View
                                    style={{
                                        width: BLOC.hauteur,
                                        height: BLOC.hauteur,
                                        padding: 5,
                                        borderWidth: 0.75,
                                        borderColor: SECONDAIRE,
                                        borderRadius: 8,
                                        backgroundColor: "#FFFFFF",
                                        marginRight: 14,
                                    }}
                                >
                                    <Image src={qrCode} style={{ width: "100%", height: "100%" }} />
                                </View>
                            )}
                            <View style={{ justifyContent: "space-between", paddingVertical: 2 }}>
                                <View>
                                    <Text style={libelle}>Issued on</Text>
                                    <Text style={{ fontWeight: 600, fontSize: 12, lineHeight: 1.1, color: ENCRE }}>
                                        {issued}
                                    </Text>
                                </View>
                                <View>
                                    <Text style={libelle}>Certificate ID</Text>
                                    <Text style={{ fontFamily: "SourceCode", fontSize: 11, lineHeight: 1.1, color: ENCRE }}>
                                        {certification.certificationNumber}
                                    </Text>
                                </View>
                            </View>
                        </View>

                        <Sceau />

                        <View
                            style={{
                                width: BLOC.largeur,
                                height: BLOC.hauteur,
                                alignItems: "flex-end",
                                justifyContent: "space-between",
                            }}
                        >
                            <Image
                                src={asset("/images/site/brand/signature.png")}
                                style={{ height: 34 }}
                            />
                            <View
                                style={{
                                    width: 170,
                                    borderTopWidth: 0.75,
                                    borderTopColor: LAVANDE_FILET,
                                    paddingTop: 5,
                                    alignItems: "flex-end",
                                }}
                            >
                                <Text style={{ fontWeight: 600, fontSize: 10.5, lineHeight: 1.1, color: ENCRE }}>
                                    Louis-Nicolas Leuillet
                                </Text>
                                <Text style={{ fontSize: 9, lineHeight: 1.1, color: DOUX }}>Founder, Believemy</Text>
                            </View>
                        </View>
                    </View>

                    <Text
                        style={{
                            position: "absolute",
                            bottom: 14,
                            left: 0,
                            right: 0,
                            textAlign: "center",
                            fontSize: 8,
                            color: DOUX,
                        }}
                    >
                        Scan the code or visit {url.replace("https://", "")} to verify this certificate.
                    </Text>
                </View>
            </Page>
        </Document>
    );
};

// `withToolbar` ajoute sous l'aperçu une barre d'actions (agrandir,
// télécharger le PDF) ; sans elle, une pastille « Agrandir » apparaît au
// survol.
export default function CertificationDocument({ certification, withToolbar = false }) {
    const t = useTranslations();
    const [imgUrl, setImgUrl] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    // Avancement de la préparation (0 à 1), relevé à chaque étape : il colore
    // peu à peu l'animation d'attente.
    const [progress, setProgress] = useState(0);
    // Le PDF généré pour l'aperçu sert aussi au téléchargement depuis la
    // fenêtre d'agrandissement : on garde son adresse locale.
    const [pdfUrl, setPdfUrl] = useState(null);
    const [isOpen, setIsOpen] = useState(false);

    // Le document ne se génère que si les données sont complètes.
    const isValid = Boolean(
        certification && certification.user && certification.course,
    );

    useEffect(() => {
        let canceled = false;
        if (!isValid) return;

        setIsLoading(true);
        setProgress(0.08);

        (async () => {
            try {
                // 1) Génère le QR code puis le PDF côté client
                const qrCode = await certificateQrCode(
                    certification.certificationNumber,
                );
                if (!canceled) setProgress(0.25);
                const blob = await pdf(
                    <Certification
                        certification={certification}
                        qrCode={qrCode}
                    />,
                ).toBlob();
                const arrayBuffer = await blob.arrayBuffer();
                if (!canceled) {
                    setPdfUrl(URL.createObjectURL(blob));
                    setProgress(0.55);
                }

                // 2) Charge PDF.js à la volée et attache un worker local (pas de CDN)
                const pdfjsLib = await import("pdfjs-dist");
                if (
                    pdfjsLib?.GlobalWorkerOptions &&
                    !pdfjsLib.GlobalWorkerOptions.workerPort
                ) {
                    try {
                        // Attache un Module Worker packagé par Next (pas de fetch public requis)
                        const moduleUrl = new URL(
                            "pdfjs-dist/build/pdf.worker.mjs",
                            import.meta.url,
                        );
                        const worker = new Worker(moduleUrl, {
                            type: "module",
                        });
                        pdfjsLib.GlobalWorkerOptions.workerPort = worker;
                    } catch (_) {
                        // Laisser pdf.js utiliser le fake worker en dernier recours
                    }
                }

                // 3) Ouvre le document et rend la 1ère page sur un canvas
                const loadingTask = pdfjsLib.getDocument({ data: arrayBuffer });
                const pdfDocument = await loadingTask.promise;
                const page = await pdfDocument.getPage(1);
                if (!canceled) setProgress(0.8);

                const scale = 3; // qualité de rendu (3x)
                const viewport = page.getViewport({ scale });

                const canvas = document.createElement("canvas");
                const context = canvas.getContext("2d");
                canvas.width = viewport.width;
                canvas.height = viewport.height;

                await page.render({ canvasContext: context, viewport }).promise;

                if (canceled) return;
                setProgress(1);

                const dataUrl = canvas.toDataURL("image/png");
                setImgUrl(dataUrl);
            } catch (error) {
                console.error(
                    "Erreur lors de la génération de l'image:",
                    error,
                );
            } finally {
                if (!canceled) {
                    setIsLoading(false);
                }
            }
        })();

        return () => {
            canceled = true;
        };
    }, [certification, isValid]);

    // L'adresse locale du PDF est rendue quand le composant disparaît.
    useEffect(() => {
        return () => {
            if (pdfUrl) URL.revokeObjectURL(pdfUrl);
        };
    }, [pdfUrl]);

    if (!isValid) return <div>Invalid data</div>;

    const name = [certification.user.firstName, certification.user.lastName]
            .filter(Boolean)
            .join(" ");
    const alt = t("certification.certification_documentAlt", { name });

    return (
        <>
            <button
                type="button"
                onClick={() => imgUrl && setIsOpen(true)}
                disabled={!imgUrl}
                aria-label={t("certification.certification_enlarge")}
                className={`group relative block w-full cursor-zoom-in overflow-hidden rounded-2xl text-left disabled:cursor-default ${
                    withToolbar
                        ? "shadow-[0_24px_48px_-28px_rgba(40,30,90,0.45)]"
                        : ""
                }`}
            >
                <div className="aspect-[1.4142857142857142/1] relative">
                    {/* Démonté une fois l'image affichée : l'animation ne tourne pas dans le vide. */}
                    <AnimatePresence>
                        {isLoading && (
                            <motion.div
                                className="absolute inset-0 z-10"
                                exit={{ opacity: 0 }}
                                transition={{ duration: 0.5, ease: "easeOut" }}
                            >
                                <CertificationLoader
                                    label={t("certification.certification_loading")}
                                    progress={progress}
                                />
                            </motion.div>
                        )}
                    </AnimatePresence>

                    {imgUrl && (
                        <img
                            src={imgUrl}
                            alt={alt}
                            className={`w-full h-auto transition-opacity duration-700 ease-out ${
                                !isLoading ? "opacity-100" : "opacity-0"
                            }`}
                            style={{ aspectRatio: "1.4142857142857142/1" }}
                            onLoad={() => setIsLoading(false)}
                        />
                    )}
                </div>
                {imgUrl && !withToolbar && (
                    <span className="pointer-events-none absolute right-3 top-3 flex items-center gap-1.5 rounded-full bg-white/90 px-3 py-1.5 text-xs font-semibold text-slate-700 opacity-0 shadow-sm ring-1 ring-slate-900/5 backdrop-blur transition-opacity duration-200 group-hover:opacity-100 group-focus-visible:opacity-100">
                        <SolarIcon name="magnifer-zoom-in-linear" className="text-sm" />
                        {t("certification.certification_enlarge")}
                    </span>
                )}
            </button>

            {withToolbar && (
                <div className="mt-3 flex flex-wrap items-center justify-center gap-1 text-sm">
                    <button
                        type="button"
                        onClick={() => imgUrl && setIsOpen(true)}
                        disabled={!imgUrl}
                        className="inline-flex cursor-pointer items-center gap-2 rounded-full px-3.5 py-2 font-medium text-slate-600 transition-colors hover:bg-white hover:text-slate-900 disabled:cursor-default disabled:opacity-50"
                    >
                        <SolarIcon name="magnifer-zoom-in-linear" className="text-base" />
                        {t("certification.certification_enlarge")}
                    </button>
                    {pdfUrl ? (
                        <a
                            href={pdfUrl}
                            download={`certification-believemy-${certification.certificationNumber}.pdf`}
                            className="inline-flex items-center gap-2 rounded-full px-3.5 py-2 font-medium text-slate-600 transition-colors hover:bg-white hover:text-slate-900"
                        >
                            <SolarIcon name="download-minimalistic-linear" className="text-base" />
                            {t("certification.certification_download")}
                        </a>
                    ) : (
                        <span className="inline-flex items-center gap-2 rounded-full px-3.5 py-2 font-medium text-slate-400">
                            <SolarIcon name="download-minimalistic-linear" className="text-base" />
                            {t("certification.certification_download")}
                        </span>
                    )}
                </div>
            )}

            <BottomSheetModal
                isOpen={isOpen}
                onClose={() => setIsOpen(false)}
                maxWidthClass="sm:max-w-5xl"
                labelledBy="certification-modal-title"
                portal
            >
                <div className="p-4 sm:p-6">
                    <div className="mb-4 flex items-center justify-between gap-4">
                        <p
                            id="certification-modal-title"
                            className="mb-0 min-w-0 truncate font-semibold text-slate-900 dark:text-white"
                        >
                            {alt}
                        </p>
                        <div className="flex shrink-0 items-center gap-2">
                            {pdfUrl && (
                                <a
                                    href={pdfUrl}
                                    download={`certification-believemy-${certification.certificationNumber}.pdf`}
                                    className="inline-flex items-center gap-2 rounded-full border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-900 transition-colors hover:border-slate-400 hover:bg-slate-50 dark:border-white/20 dark:text-white dark:hover:bg-white/10"
                                >
                                    <SolarIcon
                                        name="file-download-bold-duotone"
                                        className="text-lg text-primary"
                                    />
                                    <span className="hidden sm:inline">
                                        {t("certification.certification_download")}
                                    </span>
                                    <span className="sm:hidden">PDF</span>
                                </a>
                            )}
                            <button
                                type="button"
                                onClick={() => setIsOpen(false)}
                                aria-label={t("certification.certification_close")}
                                className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-full text-slate-500 transition-colors hover:bg-slate-100 dark:hover:bg-white/10"
                            >
                                <SolarIcon name="close-circle-linear" className="text-xl" />
                            </button>
                        </div>
                    </div>
                    {imgUrl && (
                        <img
                            src={imgUrl}
                            alt={alt}
                            className="w-full h-auto rounded-xl ring-1 ring-slate-900/5"
                            style={{ aspectRatio: "1.4142857142857142/1" }}
                        />
                    )}
                </div>
            </BottomSheetModal>
        </>
    );
}
